import * as THREE from 'three'
import type { FloorLayout } from './types'
import { CORRIDOR_MARGIN, DIRECTORY_PYLON, SLOT_WIDTH } from './mallLayout'
import { MALL, makeContactShadowTexture, makeDirectoryTexture } from './mallTextures'
import type { MallFinishes } from './mallArchitecture'

/**
 * Concourse furniture — planters, seating and the directory pylon.
 *
 * These matter more than they look like they should. An empty corridor of
 * shopfronts reads as a corridor; the moment there are objects on the
 * centreline to walk around, at a rhythm slower than the bay rhythm, it starts
 * to read as a place. They're also what gives you a sense of your own speed.
 */

/** A cylinder you can't walk through, in world XZ. */
export interface Obstacle {
  x: number
  z: number
  radius: number
}

export interface BuiltFixtures {
  group: THREE.Group
  obstacles: Obstacle[]
  dispose: () => void
}

export function buildFixtures(floor: FloorLayout, fin: MallFinishes): BuiltFixtures {
  const group = new THREE.Group()
  const obstacles: Obstacle[] = []
  const owned: Array<{ dispose: () => void }> = []

  const shadowTex = makeContactShadowTexture()
  owned.push(shadowTex)
  const shadowMat = new THREE.MeshBasicMaterial({
    map: shadowTex, transparent: true, opacity: 0.55, depthWrite: false, blending: THREE.MultiplyBlending,
  })
  const shadowGeo = new THREE.PlaneGeometry(1, 1)
  owned.push(shadowMat, shadowGeo)

  const dropShadow = (x: number, z: number, size: number) => {
    const s = new THREE.Mesh(shadowGeo, shadowMat)
    s.rotation.x = -Math.PI / 2
    s.scale.set(size, size, 1)
    s.position.set(x, 0.008, z)
    group.add(s)
  }

  const kit = new FixtureKit()
  owned.push(kit)

  const bays = Math.max(1, Math.round((floor.corridorLength - CORRIDOR_MARGIN) / SLOT_WIDTH))
  const x0 = floor.originX

  // Alternate planter / bench pair every other bay, on the centreline
  for (let i = 1; i < bays; i += 2) {
    const z = -CORRIDOR_MARGIN - i * SLOT_WIDTH
    if ((i - 1) % 4 === 0) {
      group.add(makePlanter(kit, x0, z))
      dropShadow(x0, z, 3.0)
      obstacles.push({ x: x0, z, radius: 1.25 })
    } else {
      // Benches sit back-to-back either side of the centreline, as they do in
      // real concourses, so both directions of travel get a seat.
      for (const s of [-1, 1]) {
        group.add(makeBench(kit, x0, z + s * 0.42, s))
        obstacles.push({ x: x0, z: z + s * 0.42, radius: 1.15 })
      }
      dropShadow(x0, z, 3.0)
    }
  }

  group.add(makeDirectoryPylon(kit, floor, fin, owned))
  obstacles.push({ x: x0 + DIRECTORY_PYLON.dx, z: DIRECTORY_PYLON.z, radius: 0.8 })

  return {
    group,
    obstacles,
    dispose: () => owned.forEach((o) => o.dispose()),
  }
}

/** Geometry/material shared across every fixture on a floor. */
class FixtureKit {
  bowl = new THREE.CylinderGeometry(0.98, 0.82, 0.56, 24)
  rim = new THREE.CylinderGeometry(1.02, 1.02, 0.09, 24)
  soil = new THREE.CircleGeometry(0.9, 20)
  leaf = new THREE.IcosahedronGeometry(1, 1)
  stem = new THREE.CylinderGeometry(0.035, 0.05, 1, 5)

  plinth = new THREE.BoxGeometry(0.34, 0.42, 0.52)
  seat = new THREE.BoxGeometry(2.1, 0.11, 0.5)
  slatGap = new THREE.BoxGeometry(2.1, 0.02, 0.06)

  pylonBase = new THREE.BoxGeometry(0.95, 0.1, 0.44)
  pylonBody = new THREE.BoxGeometry(0.86, 2.1, 0.16)
  pylonScreen = new THREE.PlaneGeometry(0.76, 1.86)

  stoneMat = new THREE.MeshStandardMaterial({ color: '#d3c9b9', roughness: 0.72, metalness: 0.03, envMapIntensity: 0.6 })
  graniteMat = new THREE.MeshStandardMaterial({ color: MALL.granite, roughness: 0.22, metalness: 0.25, envMapIntensity: 1.0 })
  soilMat = new THREE.MeshStandardMaterial({ color: MALL.soil, roughness: 1 })
  foliageMat = new THREE.MeshStandardMaterial({ color: MALL.foliage, roughness: 0.85, flatShading: true })
  foliageDeepMat = new THREE.MeshStandardMaterial({ color: MALL.foliageDeep, roughness: 0.9, flatShading: true })
  woodMat = new THREE.MeshStandardMaterial({ color: MALL.wood, roughness: 0.55, metalness: 0.05, envMapIntensity: 0.5 })
  metalMat = new THREE.MeshStandardMaterial({ color: MALL.bronze, roughness: 0.35, metalness: 0.85, envMapIntensity: 1.1 })

  dispose() {
    ;[this.bowl, this.rim, this.soil, this.leaf, this.stem, this.plinth, this.seat, this.slatGap,
      this.pylonBase, this.pylonBody, this.pylonScreen].forEach((g) => g.dispose())
    ;[this.stoneMat, this.graniteMat, this.soilMat, this.foliageMat, this.foliageDeepMat,
      this.woodMat, this.metalMat].forEach((m) => m.dispose())
  }
}

function makePlanter(kit: FixtureKit, x: number, z: number): THREE.Group {
  const g = new THREE.Group()
  g.position.set(x, 0, z)

  const bowl = new THREE.Mesh(kit.bowl, kit.stoneMat)
  bowl.position.y = 0.28
  g.add(bowl)

  const rim = new THREE.Mesh(kit.rim, kit.graniteMat)
  rim.position.y = 0.58
  g.add(rim)

  const soil = new THREE.Mesh(kit.soil, kit.soilMat)
  soil.rotation.x = -Math.PI / 2
  soil.position.y = 0.56
  g.add(soil)

  // Ficus-ish massing: a few stems carrying clustered foliage at varied heights
  const stems: Array<[number, number, number]> = [[0, 1.15, 0], [0.32, 0.85, 0.2], [-0.3, 0.95, -0.22]]
  stems.forEach(([sx, h, sz], i) => {
    const stem = new THREE.Mesh(kit.stem, kit.woodMat)
    stem.scale.y = h
    stem.position.set(sx, 0.56 + h / 2, sz)
    g.add(stem)

    const canopy = new THREE.Mesh(kit.leaf, i % 2 === 0 ? kit.foliageMat : kit.foliageDeepMat)
    canopy.scale.setScalar(0.42 + i * 0.05)
    canopy.position.set(sx, 0.56 + h + 0.2, sz)
    g.add(canopy)
  })
  // Low understorey spilling over the rim
  for (let i = 0; i < 5; i++) {
    const a = (i / 5) * Math.PI * 2
    const shrub = new THREE.Mesh(kit.leaf, i % 2 === 0 ? kit.foliageDeepMat : kit.foliageMat)
    shrub.scale.set(0.34, 0.2, 0.34)
    shrub.position.set(Math.cos(a) * 0.55, 0.66, Math.sin(a) * 0.55)
    g.add(shrub)
  }

  return g
}

function makeBench(kit: FixtureKit, x: number, z: number, facing: number): THREE.Group {
  const g = new THREE.Group()
  g.position.set(x, 0, z)
  g.rotation.y = facing > 0 ? 0 : Math.PI

  for (const s of [-1, 1]) {
    const plinth = new THREE.Mesh(kit.plinth, kit.graniteMat)
    plinth.position.set(s * 0.72, 0.21, 0)
    g.add(plinth)
  }

  const seat = new THREE.Mesh(kit.seat, kit.woodMat)
  seat.position.y = 0.475
  g.add(seat)

  // Slat reveals — a solid slab reads as a kerb, slats read as a bench
  for (const dz of [-0.16, 0, 0.16]) {
    const gap = new THREE.Mesh(kit.slatGap, kit.metalMat)
    gap.position.set(0, 0.531, dz)
    g.add(gap)
  }

  return g
}

/** Backlit wing directory at the concourse mouth. */
function makeDirectoryPylon(
  kit: FixtureKit,
  floor: FloorLayout,
  fin: MallFinishes,
  owned: Array<{ dispose: () => void }>
): THREE.Group {
  const g = new THREE.Group()
  g.position.set(floor.originX + DIRECTORY_PYLON.dx, 0, DIRECTORY_PYLON.z)
  g.rotation.y = DIRECTORY_PYLON.rotY

  const base = new THREE.Mesh(kit.pylonBase, kit.graniteMat)
  base.position.y = 0.05
  g.add(base)

  const body = new THREE.Mesh(kit.pylonBody, kit.metalMat)
  body.position.y = 1.18
  g.add(body)

  const tex = fin.track(makeDirectoryTexture(floor.category, floor.color, floor.slots.map((s) => s.shop.name)))
  const screenMat = new THREE.MeshBasicMaterial({ map: tex })
  owned.push(screenMat)
  for (const s of [-1, 1]) {
    const screen = new THREE.Mesh(kit.pylonScreen, screenMat)
    screen.position.set(0, 1.18, s * 0.085)
    screen.rotation.y = s > 0 ? 0 : Math.PI
    g.add(screen)
  }

  return g
}
